import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import i18n from 'i18n'
import { ChartConfig } from '../types'
import { DonutMetricItemProps, GaugeMetricItemProps } from '../charts'
import { CONVERGED_MEDIA_COLOR_SETTINGS, OVERVIEW_COLOR_SETTINGS } from './colorSettings'
import { convertToGaugeLegendData } from './convert'
import { valueFormatter, percentFormatter } from './format'
import { isConvergedMedia } from './viewOption'
import { getGaugeAxisRange } from './axis'
import { isNetSentiment } from './category'
import { isDisabled } from './disable'

const displayNAInCenter = (chartConfig: ChartConfig) => ({
  value: undefined,
  valueFormatter: () => chartConfig.value as string
})

const getColorSettings = (
  chartConfig: ChartConfig<'Donut' | 'Gauge'>,
  viewOption: InsightViewOptionStrings
) => {
  const colorSettings = isConvergedMedia(viewOption)
    ? CONVERGED_MEDIA_COLOR_SETTINGS
    : OVERVIEW_COLOR_SETTINGS[chartConfig.category]

  const newColorSettings: Record<string, string> = {}
  Object.entries(colorSettings).forEach(([key, value]) => {
    newColorSettings[i18n.t(key)] = value
  })
  return newColorSettings
}

export const createDonutOptions = (
  chartConfig: ChartConfig<'Donut'>,
  viewOption: InsightViewOptionStrings
): {
  chartOptions: DonutMetricItemProps['chartOptions']
  legendOptions: DonutMetricItemProps['legendOptions']
} => ({
  chartOptions: {
    centerSection: {
      title: chartConfig.label || '',
      deltaValue: chartConfig.delta?.value,
      deltaValueFormatter: (value) => value,
      deltaDirection: chartConfig.delta?.direction || 'none',
      value: chartConfig.value as number,
      valueFormatter,
      ...(isDisabled(chartConfig) && displayNAInCenter(chartConfig))
    },
    colorSettings: getColorSettings(chartConfig, viewOption)
  },
  legendOptions: { isShowLegend: chartConfig.showLegend }
})

export const createGaugeOptions = (
  chartConfig: ChartConfig<'Gauge'>,
  viewOption: InsightViewOptionStrings
): {
  chartOptions: GaugeMetricItemProps['chartOptions']
  legendOptions: GaugeMetricItemProps['legendOptions']
} => {
  const displayValueFormatter = isNetSentiment(chartConfig) ? percentFormatter : undefined
  const { value: centerValue } = chartConfig

  return {
    chartOptions: {
      ...getGaugeAxisRange(chartConfig.axis, isNetSentiment(chartConfig)),
      yAxisLabelFormatter: displayValueFormatter,
      centerSection: {
        title: chartConfig.label || '',
        deltaValue: chartConfig.delta?.value,
        deltaValueFormatter: (value) => value,
        deltaDirection: chartConfig.delta?.direction || 'none',
        value: centerValue as number,
        valueFormatter: displayValueFormatter,
        ...(isDisabled(chartConfig) && displayNAInCenter(chartConfig))
      },
      colorSettings: getColorSettings(chartConfig, viewOption)
    },
    legendOptions: {
      isShowLegend: chartConfig.showLegend && !!chartConfig.legendData,
      data: convertToGaugeLegendData(chartConfig)
    }
  }
}
